import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/axios';
import {
  getCachedSchoolName,
  setCachedSchoolName,
  SCHOOL_NAME_EVENT,
} from '../../utils/branding';

export default function Sidebar({ isOpen = false, onClose = () => {} }) {
  const { user } = useAuth();
  const role = user?.role;

  const [schoolName, setSchoolName] = useState(getCachedSchoolName());

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get('/public/branding');
        if (r.data?.school_name) {
          setCachedSchoolName(r.data.school_name);
          setSchoolName(r.data.school_name);
        }
      } catch {
        /* silent */
      }
    };
    load();
    const onUpdate = () => setSchoolName(getCachedSchoolName());
    window.addEventListener(SCHOOL_NAME_EVENT, onUpdate);
    return () => window.removeEventListener(SCHOOL_NAME_EVENT, onUpdate);
  }, []);

  const menuItems = [
    { label: 'Dashboard', icon: 'fa-th-large', path: '/dashboard',
      roles: ['admin', 'superintendent', 'principal', 'accountant', 'teacher', 'parent', 'librarian', 'receptionist', 'lab_attendant'] },

    // Parent-only
    { label: 'Report Card', icon: 'fa-file-alt', path: '/dashboard/report-card',
      roles: ['parent'] },
    { label: 'Attendance', icon: 'fa-calendar-check', path: '/dashboard/child-attendance',
      roles: ['parent'] },
    { label: 'Payments', icon: 'fa-money-bill-wave', path: '/dashboard/child-payments',
      roles: ['parent'] },

    // Reception
    { label: 'Contacts Directory', icon: 'fa-address-book', path: '/dashboard/contacts',
      roles: ['admin', 'superintendent', 'principal', 'receptionist'] },
    { label: 'Admission & Inquiries', icon: 'fa-clipboard-list', path: '/dashboard/inquiries',
      roles: ['admin', 'superintendent', 'principal', 'receptionist'] },

    // Admin / Principal
    { label: 'Campus', icon: 'fa-school', path: '/dashboard/campus',
      roles: ['admin', 'superintendent', 'principal'] },
    { label: 'Users', icon: 'fa-users', path: '/dashboard/users',
      roles: ['admin', 'superintendent', 'principal'] },
    { label: 'Admin Staff', icon: 'fa-user-tie', path: '/dashboard/admin-staff',
      roles: ['admin', 'superintendent', 'principal'] },
    { label: 'Teachers', icon: 'fa-chalkboard-teacher', path: '/dashboard/teachers',
      roles: ['admin', 'superintendent', 'principal'] },
    { label: 'Classes', icon: 'fa-chalkboard', path: '/dashboard/classes',
      roles: ['admin', 'superintendent', 'principal', 'teacher'] },
    { label: 'Students', icon: 'fa-user-graduate', path: '/dashboard/students',
      roles: ['admin', 'superintendent', 'principal', 'accountant', 'teacher'] },
    { label: 'Attendance', icon: 'fa-clipboard-check', path: '/dashboard/attendance',
      roles: ['admin', 'superintendent', 'principal', 'accountant', 'teacher'] },
    { label: 'Grades', icon: 'fa-star', path: '/dashboard/grades',
      roles: ['teacher'] },
    { label: 'Fees', icon: 'fa-credit-card', path: '/dashboard/fees',
      roles: ['admin', 'superintendent', 'principal', 'accountant'] },
    { label: 'Staff', icon: 'fa-users-cog', path: '/dashboard/staff',
      roles: ['admin', 'superintendent', 'principal', 'accountant'] },
    { label: 'Salaries', icon: 'fa-money-bill-wave', path: '/dashboard/salaries',
      roles: ['admin', 'accountant', 'superintendent', 'principal'] },
    { label: 'Fee Structure', icon: 'fa-layer-group', path: '/dashboard/fee-structures',
      roles: ['admin', 'accountant'] },
    { label: 'Library', icon: 'fa-book-open', path: '/dashboard/library',
      roles: ['admin', 'superintendent', 'principal', 'teacher', 'librarian'] },
    { label: 'Bulk Upload', icon: 'fa-upload', path: '/dashboard/bulk',
      roles: ['admin', 'superintendent', 'principal'] },
    { label: 'Reports', icon: 'fa-chart-bar', path: '/dashboard/reports',
      roles: ['admin', 'superintendent', 'principal', 'accountant'] },
    { label: 'Timetable', icon: 'fa-calendar-alt', path: '/dashboard/timetable',
      roles: ['principal', 'teacher'] },
    { label: 'Staff Attendance', icon: 'fa-user-clock', path: '/dashboard/staff-attendance',
      roles: ['admin', 'superintendent', 'principal', 'accountant'] },
    { label: 'Integrations', icon: 'fa-plug', path: '/dashboard/integrations',
      roles: ['admin'] },
    { label: 'Devices', icon: 'fa-fingerprint', path: '/dashboard/devices',
      roles: ['admin'] },
    { label: 'Admin Settings', icon: 'fa-cog', path: '/dashboard/admin-settings',
      roles: ['admin'] },

    // Communication
    { label: 'Notifications', icon: 'fa-bell', path: '/dashboard/notifications',
      roles: ['admin', 'superintendent', 'principal', 'accountant', 'librarian', 'receptionist', 'lab_attendant', 'teacher', 'parent'] },
    { label: 'Notes', icon: 'fa-envelope', path: '/dashboard/notes',
      roles: ['admin', 'superintendent', 'principal', 'teacher', 'parent'] },
    { label: 'My Attendance', icon: 'fa-id-badge', path: '/dashboard/my-attendance',
      roles: ['teacher', 'librarian', 'receptionist', 'lab_attendant'] },
  ];

  const visible = menuItems.filter((item) => item.roles.includes(role));

  return (
    <aside
      className={`
        w-64 bg-white shadow-md flex-shrink-0 h-screen flex flex-col z-40
        fixed lg:static top-0 left-0
        transform transition-transform duration-200 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}
    >
      <div className="p-4 border-b flex-shrink-0 flex items-center justify-between">
        <h1 className="text-xl font-bold text-blue-600 truncate">{schoolName}</h1>
        <button
          onClick={onClose}
          className="lg:hidden text-gray-400 hover:text-gray-700 p-1 -mr-1"
          aria-label="Close menu"
        >
          <i className="fas fa-times text-lg"></i>
        </button>
      </div>

      <nav className="p-2 flex-1 overflow-y-auto">
        {visible.map((item) => (
          <NavLink
            key={item.label + item.path}
            to={item.path}
            end={item.path === '/dashboard'}
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center p-3 rounded-lg transition-colors ${
                isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100'
              }`
            }
          >
            <i className={`fas ${item.icon} w-5`}></i>
            <span className="ml-3">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}